/**
 * The game sounds, played from ONE store subscription and refs — never from
 * render (UX spec §1 rule 8 / §6 rule 8).
 *
 * `use-sound` is the app's existing player (PlayChess L899-911 uses the same
 * hook and the same four local files plus the three S3 URLs), so the assets
 * are already warm in the browser cache for users who came from the old lane.
 *
 * Every trigger is a RISING EDGE on a value the reducer already owns, tracked
 * in refs, so a re-render of this hook's host cannot replay a sound.
 *
 * ONE EXCEPTION, and it is not an edge: the 10 s FIRST-MOVE ALARM (P1-5,
 * decision D1). It is a LOOPING sound that starts in the last 10 s of a first
 * move I owe and stops the moment the move lands, the deadline is disarmed, or
 * the game ends — the old `ClockComponent` L156-200 plays
 * `game_resign_countdown_sound.mp3` with `loop: true` on exactly that
 * condition, and the web lane had no alarm at all (the app lane did). The
 * `deadline` it is keyed on takes the place of a rising edge: one alarm per
 * deadline, never two.
 */
import {useEffect, useRef} from 'react';
import useSound from 'use-sound';

// The bundler resolves .mp3 through its asset loader; there is no ambient
// `declare module '*.mp3'` in this repo, so every sound import in the app
// carries this suppression (src/Atoms/Header, src/Screens/Books,
// src/Hooks/FunChallengesSounds all import the same way).
// @ts-ignore
import confirmationSound from '@/Assets/Sounds/Confirmation.mp3';
// @ts-ignore
import drawOfferSound from '@/Assets/Sounds/draw_offer.mp3';
// @ts-ignore
import gameStartSound from '@/Assets/Sounds/game_start.mp3';
// The 10 s first-move alarm — the same file the old ClockComponent loops (L7).
// @ts-ignore
import firstMoveAlarmSound from '@/Assets/Sounds/game_resign_countdown_sound.mp3';
// M4: the tournament round sting, the same file the old screen plays (L124).
// @ts-ignore
import roundStartSound from '@/Assets/Sounds/round_start.mp3';
import {owesFirstMove} from '@/Store/liveGame/chess/firstMove';
import {selectMyTurn} from '@/Store/liveGame/selectors';
import {liveGameStore} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';

import {useV2Ui} from '../theme/useV2Ui';

// The three end-of-game stings live on S3 (PlayChess L901-910) — same URLs.
const VICTORY_URL =
  'https://cc-home.s3.ap-south-1.amazonaws.com/sound-effects/custom-app-move-sound/victory.mp3';
const DEFEAT_URL =
  'https://cc-home.s3.ap-south-1.amazonaws.com/sound-effects/custom-app-move-sound/Defeat_pitch_shifted.mp3';
const DRAW_URL =
  'https://cc-home.s3.ap-south-1.amazonaws.com/sound-effects/custom-app-move-sound/Draw_pitch_shifted.mp3';

/** PlayChess L2467-2469: `setTimeout(gamestartSound, 1500)`. */
const START_DELAY_MS = 1500;

/** ClockComponent L190: the alarm starts with 10 s of the first move left. */
const FIRST_MOVE_ALARM_MS = 10000;
/** the alarm must also start when nothing else in the store moves */
const ALARM_TICK_MS = 500;

export function useSounds(): void {
  const [playStart] = useSound(gameStartSound);
  const [playRoundStart] = useSound(roundStartSound);
  const [playDrawOffer] = useSound(drawOfferSound);
  const [playConfirmation] = useSound(confirmationSound);
  const [playVictory] = useSound(VICTORY_URL);
  const [playDefeat] = useSound(DEFEAT_URL);
  const [playDraw] = useSound(DRAW_URL);
  // ClockComponent L158-164: volume 1, LOOPING, stopped explicitly.
  const [playFirstMoveAlarm, {stop: stopFirstMoveAlarm}] = useSound(
    firstMoveAlarmSound,
    {volume: 1, loop: true},
  );

  // one ref bag so the subscription below can be registered exactly once
  const players = useRef({
    playStart,
    playRoundStart,
    playDrawOffer,
    playConfirmation,
    playVictory,
    playDefeat,
    playDraw,
    playFirstMoveAlarm,
    stopFirstMoveAlarm,
  });
  players.current = {
    playStart,
    playRoundStart,
    playDrawOffer,
    playConfirmation,
    playVictory,
    playDefeat,
    playDraw,
    playFirstMoveAlarm,
    stopFirstMoveAlarm,
  };

  useEffect(() => {
    let lastReadyAt: number | null = null;
    /** PlayChess L2467-2469 plays the start sting 1.5 s after game_ready. */
    let startTimer: any = null;
    let lastResultKey: string | null = null;
    let lastDrawOffered = false;
    let lastRejectedAt: number | null = null;
    /**
     * M4: the round sting. The old screen plays it inside `pairingCallback`;
     * here the edge is `tournament.roundStartAt`, which that same broadcast is
     * the only writer of (`reducers/tournament.pairing`). ONE audio owner per
     * lane, so it lives here and not in `hooks/useTournamentEffects`.
     */
    let lastRoundStartAt: number | null = null;
    /** the deadline the alarm is currently sounding for (null = silent) */
    let alarmedDeadline: number | null = null;

    /**
     * The 10 s first-move alarm. "Owed" is the shared rule
     * (`chess/firstMove.ts`), so this and `stage/PlayerRow/FirstMoveBanner`
     * can never disagree about whose move is missing; MY TURN is the second
     * half of the old gate — the old clock only runs (and therefore only
     * alarms) while `myTurn && playing` (ClockComponent L165-175), so a player
     * waiting for white's opening move hears nothing even though their own
     * first move is "owed".
     *
     * NO MODE GATE (D1): it sounds in tournament and arena games too, where the
     * server aborts at TC/2 for a missing first move just the same. The old
     * screens hid it there only because they keyed the banner on `showAbort`.
     *
     * It is NOT inside the mute early-return of `apply`: a mute must STOP a
     * sounding alarm, not merely skip the next start.
     */
    const onFirstMoveAlarm = (state: LiveGameState) => {
      const deadline = state.game.firstMoveDeadlineMs;
      const owed =
        state.game.phase === 'playing' &&
        selectMyTurn(state) &&
        owesFirstMove({
          moves: state.game.moves.length,
          myColor: state.game.myColor,
          isBlackTurnFirst: state.game.isBlackTurnFirst,
        });
      const audible = useV2Ui.getState().soundOn;

      if (!deadline || !owed || !audible) {
        if (alarmedDeadline !== null) {
          alarmedDeadline = null;
          players.current.stopFirstMoveAlarm();
        }
        return;
      }
      const left = deadline - Date.now();
      if (left > FIRST_MOVE_ALARM_MS || left <= 0) {
        return;
      }
      if (alarmedDeadline === deadline) {
        return;
      }
      alarmedDeadline = deadline;
      players.current.playFirstMoveAlarm();
    };

    const apply = (state: LiveGameState) => {
      onFirstMoveAlarm(state);
      if (!useV2Ui.getState().soundOn) {
        // keep the edges in sync while muted, so unmuting does not replay
        lastReadyAt = state.game.readyAt;
        lastResultKey = state.game.result?.key ?? null;
        lastDrawOffered = state.game.drawOffered;
        lastRejectedAt = state.game.drawRejectedAt;
        lastRoundStartAt = state.tournament.roundStartAt;
        return;
      }

      if (
        state.tournament.roundStartAt &&
        state.tournament.roundStartAt !== lastRoundStartAt
      ) {
        players.current.playRoundStart();
      }
      lastRoundStartAt = state.tournament.roundStartAt;

      if (state.game.readyAt && state.game.readyAt !== lastReadyAt) {
        lastReadyAt = state.game.readyAt;
        // PlayChess L2465-2469: the sting lands 1.5 s into the countdown, not on
        // the message — the board is still being dealt at that point.
        clearTimeout(startTimer);
        startTimer = setTimeout(() => {
          // re-check: the tile may have muted us inside the 1.5 s
          if (useV2Ui.getState().soundOn) {
            players.current.playStart();
          }
        }, START_DELAY_MS);
      }

      const result = state.game.result;
      if (result && result.key !== lastResultKey) {
        lastResultKey = result.key;
        if (result.outcome === 'Win') {
          players.current.playVictory();
        } else if (result.outcome === 'Loss') {
          players.current.playDefeat();
        } else if (result.outcome === 'Draw') {
          players.current.playDraw();
        }
      }
      if (!result) {
        // fix AA review (2026-10-06): a bye no longer fabricates a
        // `game.result` (it is a tournament event, not a game) — this is
        // intentionally SILENT, not a missed cue: a bye is not a win, and
        // the round-start sting above (`playRoundStart`, off
        // `roundStartAt`) already marks the round for everyone.
        lastResultKey = null;
      }

      if (state.game.drawOffered && !lastDrawOffered) {
        players.current.playDrawOffer();
      }
      lastDrawOffered = state.game.drawOffered;

      if (
        state.game.drawRejectedAt &&
        state.game.drawRejectedAt !== lastRejectedAt
      ) {
        players.current.playConfirmation();
      }
      lastRejectedAt = state.game.drawRejectedAt;
    };

    // seed the edges from the current state so a remount is silent
    const seed = liveGameStore.getState();
    lastReadyAt = seed.game.readyAt;
    lastResultKey = seed.game.result?.key ?? null;
    lastDrawOffered = seed.game.drawOffered;
    lastRejectedAt = seed.game.drawRejectedAt;
    lastRoundStartAt = seed.tournament.roundStartAt;

    const unsubscribe = liveGameStore.subscribe(apply);
    // the last 10 s arrive without any store write, so the alarm needs a tick
    const alarmInterval = setInterval(
      () => onFirstMoveAlarm(liveGameStore.getState()),
      ALARM_TICK_MS,
    );
    return () => {
      unsubscribe();
      clearInterval(alarmInterval);
      clearTimeout(startTimer);
      players.current.stopFirstMoveAlarm();
    };
  }, []);
}

export default useSounds;
